import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { useLocation } from 'react-router';

import { isLocalKiosk } from 'modules/dealers/selectors';
import { isCheckinSelfService } from 'modules/selfServices/selectors';

import PageBaseProps from '../../../types/PageBase';
import useScrollContent from '../../../hooks/useScrollContent';
import Icon from '../Icon';
import Button, { ButtonProps, ButtonType } from '../Button';
import ReviewCheckInButton from './ReviewCheckInButton';


type PageFooterProps = {
  shouldDisplayBackButton?: boolean;
  hideNextButton?: boolean;
  nextText?: MessageDescriptor;
  hint?: React.ReactNode;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
} & ButtonProps & PageBaseProps;

const Footer: React.FC<React.PropsWithChildren<PageFooterProps>> = ({
  shouldDisplayBackButton,
  onPrev,
  onNext,
  isPenultimate,
  hideNextButton,
  children,
  hint,
  nextText = { id: 'steps.next', defaultMessage: 'Next' },
  loading,
  wrapperProps,
  ...rest
}) => {
  const { canScroll, handleScroll } = useScrollContent();
  const location = useLocation();
  const allowReviewCheckIn = location.state?.allowReviewCheckIn;

  const isKiosk = useSelector(isLocalKiosk);
  const isCheckin = useSelector(isCheckinSelfService);

  const displayBackButton = shouldDisplayBackButton && isKiosk;

  const shouldShowFinish = isPenultimate && isCheckin;

  return (
    <footer
      {...wrapperProps}
      id="footer"
      className={classNames(
        'nextButton kiosk:z-40',
        { 'flex-col': Boolean(hint) },
        wrapperProps?.className,
      )}
    >
      {canScroll && (
        <div className="absolute -top-btn-scroll scrollBtn " aria-hidden="true">
          <div className="relative rounded-button border-4 border-white">
            <Button
              onClick={handleScroll}
              testId="footer-scroll-btn"
              className="p-xs! flex animate-bounceOnce"
            >
              <Icon className="fb-icon--arrow-down text-icon"/>
            </Button>
          </div>
        </div>
      )}
      {hint && <div className="p-3xs text-xxs text-center">{hint}</div>}
      <div className={classNames(
        'w-full flex items-center justify-center mt-2xs gap-8',
        displayBackButton ? 'kiosk:justify-between' : 'kiosk:justify-end',
      )}
      >
        {displayBackButton && !allowReviewCheckIn && (
          <Button
            type={ButtonType.SECONDARY}
            onClick={onPrev}
            testId="footer-prev-step"
            className="footer-button"
            disabled={loading}
          >
            <FormattedMessage
              id="steps.back"
              defaultMessage="Back"
            />
          </Button>
        )}
        {displayBackButton && allowReviewCheckIn && (
          <ReviewCheckInButton />
        )}
        {!hideNextButton && (
          <Button
            onClick={onNext}
            testId="next-step"
            className="footer-button"
            loading={loading}
            {...rest}
          >
            {shouldShowFinish && <FormattedMessage id="steps.finish" defaultMessage="Finish" />}
            {!shouldShowFinish && <FormattedMessage {...nextText} />}
          </Button>
        )}
        {children}
      </div>
    </footer>
  );
};

export default Footer;
